import { toast } from 'react-toastify';

import {
  balanceFactory,
  financialTransactionFactory,
  reportStatusFactory,
} from 'factories';
import { formatTimestamp } from 'helpers';
import fileDownload from 'js-file-download';
import { requestAxios } from 'services';
import {
  Balance,
  ExportToExcelParams,
  FinancialTransactions,
  GenerateReportParams,
  GetTransactionsParams,
  StoreSaleFinancialReportStatusData,
} from 'types';

async function getTransactions({
  startDate,
  endDate,
  storeNames,
  pageSize,
  currentPage,
}: GetTransactionsParams): Promise<FinancialTransactions> {
  let result: any;

  const params = {
    startDate,
    endDate,
    storeNames: storeNames.join(','),
    pageSize,
    currentPage,
  };

  try {
    const response = await requestAxios.get('reports/transactions', {
      params,
    });

    result = response.data;
  } catch (error) {
    console.error(error);
  }

  return financialTransactionFactory(result);
}

async function exportToExcel({
  startDate,
  endDate,
  storeNames,
}: ExportToExcelParams): Promise<void> {
  const params = {
    startDate,
    endDate,
    storeNames: storeNames.join(','),
  };

  return requestAxios
    .get('reports/transactions/export', {
      params,
      responseType: 'blob',
    })
    .then(response => {
      function formatDate(date: number) {
        const formattedDate = formatTimestamp(date).formattedDate;

        return formattedDate.replaceAll('/', '');
      }

      const { startDate, endDate } = params;
      const formattedStartDate = formatDate(startDate);
      const formattedEndDate = formatDate(endDate);
      const fileName = `${formattedStartDate}_${formattedEndDate}_transacoes_financeiras.csv`;

      fileDownload(response.data, fileName);
    })
    .catch(reason => {
      console.error(reason);

      toast.error('Não foi possível baixar o arquivo', {
        autoClose: false,
        hideProgressBar: false,
      });
    });
}

async function getBalance(
  storeNames: string[],
  fields: 'BASIC' | 'FULL'
): Promise<Balance> {
  let result: any;

  const params = {
    storeNames: storeNames.join(','),
    fields,
  };

  try {
    const response = await requestAxios.get('reports/transactions/balance', {
      params,
    });

    result = response.data.data[0];
  } catch (error) {
    console.error(error);
  }

  return balanceFactory(result, fields);
}

async function generateReport({
  startDate,
  endDate,
  storeName,
}: GenerateReportParams): Promise<StoreSaleFinancialReportStatusData> {
  const response = await requestAxios.get('/v2/reports/transactions/generate', {
    params: {
      startDate,
      endDate,
      storeName,
    },
  });

  return reportStatusFactory(response.data);
}

async function getReportStatus(
  storeName: string
): Promise<StoreSaleFinancialReportStatusData> {
  const response = await requestAxios.get('/v2/reports/transactions/status', {
    params: { storeName },
  });

  return reportStatusFactory(response.data);
}

async function downloadReport(storeName: string): Promise<void> {
  return requestAxios
    .get('/v2/reports/transactions/export', {
      params: { storeName },
      responseType: 'blob',
    })
    .then(response => {
      const fileName = `relatorio_transacoes_${storeName}.csv`;
      fileDownload(response.data, fileName);
    })
    .catch(reason => {
      console.error(reason);

      toast.error('Não foi possível baixar o relatório', {
        autoClose: false,
        hideProgressBar: false,
      });
    });
}

export const reportsTransactionsService = {
  getTransactions,
  exportToExcel,
  getBalance,
  generateReport,
  getReportStatus,
  downloadReport,
};
